import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#observePersonalisationReady();
  }

  #controller = new AbortController();
  #personalisationObserver = null;
  #wasPersonalisationReady = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#personalisationObserver?.disconnect();
    this.#personalisationObserver = null;
  }

  #observePersonalisationReady() {
    const productInformation =
      this.closest('[data-testid="product-information"]') ||
      this.closest('.shopify-section')?.querySelector('[data-testid="product-information"]');
    if (!(productInformation instanceof HTMLElement)) return;

    this.#personalisationObserver?.disconnect();
    this.#wasPersonalisationReady = productInformation.classList.contains('personalisation-ready');

    if (this.#wasPersonalisationReady) {
      this.#returnToPrimarySlide();
    }

    this.#personalisationObserver = new MutationObserver(() => {
      const isReady = productInformation.classList.contains('personalisation-ready');
      if (isReady && !this.#wasPersonalisationReady) {
        this.#returnToPrimarySlide();
      }
      this.#wasPersonalisationReady = isReady;
    });

    this.#personalisationObserver.observe(productInformation, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  #returnToPrimarySlide() {
    const slideshow = this.slideshow;
    if (typeof slideshow?.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }

    const slideTrack = this.querySelector('slideshow-slides');
    if (slideTrack instanceof HTMLElement) {
      slideTrack.scrollLeft = 0;
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const personalisedMediaState = this.#getPersonalisedMediaState();
    const productId = personalisedMediaState?.productId;
    const incomingProductId = source.querySelector('variant-picker')?.dataset.productId;
    if (
      productId && !event.detail.data.newProduct &&
      String(event.detail.data.productId || '') === productId &&
      String(incomingProductId || '') === productId
    ) {
      // Finish and size changes update the existing portrait surface in place.
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  #getPersonalisedMediaState() {
    const productInformation =
      this.closest('[data-testid="product-information"]') ||
      this.closest('.shopify-section')?.querySelector('[data-testid="product-information"]');
    if (!(productInformation instanceof HTMLElement)) return null;
    if (!productInformation.classList.contains('personalisation-ready')) return null;

    const personalisationBlock = productInformation.querySelector('[data-personalisation-block]');
    let productId = '';
    try {
      productId = String(JSON.parse(personalisationBlock?.querySelector('[data-personalisation-config]')?.textContent || '{}').productId || '');
    } catch { return null; }
    if (!productId) return null;
    const portraitOutput = /** @type {HTMLInputElement | null} */ (
      productInformation.querySelector('[data-personalisation-output]')
    );
    const portraitValue = String(
      portraitOutput?.value || personalisationBlock?.getAttribute('data-generated-image-url') || ''
    ).trim();
    if (!portraitValue && !productInformation.classList.contains('personalisation-resizing')) return null;

    const currentImage =
      this.querySelector('.pop-pdp-primary-slide img.product-media__image') ||
      this.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
      this.querySelector('.media-gallery__grid .product-media-container--image:first-child img.product-media__image') ||
      this.querySelector('img.product-media__image');
    if (!(currentImage instanceof HTMLImageElement)) return null;

    const activeSrc = String(currentImage.currentSrc || currentImage.getAttribute('src') || '').trim();
    if (!activeSrc) return null;

    return { productId };
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const slideshow = this.slideshow;
    if (typeof slideshow?.select === 'function') {
      slideshow.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
